import { useState } from 'react';
import {
  Stack, Group, Button, Text, Paper, SimpleGrid, Badge, Table,
  Progress, Modal, Select, Textarea, Alert, Loader, Center, Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { NumberInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconBeach, IconPlus, IconAlertCircle } from '@tabler/icons-react';
import api from '@/config/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

interface PTOBalance {
  leave_type: string;
  available: number;
  used: number;
  total: number;
  pending: number;
}

interface PTORequest {
  id: number;
  leave_type: string;
  start_date: string;
  end_date: string;
  hours_per_day: number;
  total_hours?: number;
  reason: string;
  status: string;
  created_at: string;
}

function statusColor(status: string): string {
  switch (status) {
    case 'approved':
      return 'green';
    case 'pending':
      return 'yellow';
    case 'rejected':
      return 'red';
    case 'cancelled':
      return 'gray';
    default:
      return 'gray';
  }
}

const LEAVE_TYPE_OPTIONS = [
  { value: 'vacation', label: 'Vacation' },
  { value: 'sick', label: 'Sick' },
  { value: 'personal', label: 'Personal' },
];

export default function MyPTORequests() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  // Form state
  const [leaveType, setLeaveType] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [hoursPerDay, setHoursPerDay] = useState<number | ''>(8);
  const [reason, setReason] = useState('');

  // ---------- Queries ----------

  const balanceQuery = useQuery<{ balances: PTOBalance[] }>({
    queryKey: ['my-pto-balance'],
    queryFn: async () => {
      const res = await api.get('/time-attendance/my/pto-balance');
      return res.data;
    },
  });

  const requestsQuery = useQuery<PTORequest[]>({
    queryKey: ['my-pto-requests'],
    queryFn: async () => {
      const res = await api.get('/time-attendance/pto/requests');
      const data = res.data;
      return Array.isArray(data) ? data : data.items ?? [];
    },
  });

  // ---------- Mutations ----------

  const submitMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/time-attendance/pto/request', {
        leave_type: leaveType,
        start_date: dayjs(startDate).format('YYYY-MM-DD'),
        end_date: dayjs(endDate).format('YYYY-MM-DD'),
        hours_per_day: hoursPerDay,
        reason,
      });
      return res.data;
    },
    onSuccess: () => {
      notifications.show({
        title: 'PTO Requested',
        message: 'Your PTO request has been submitted.',
        color: 'green',
      });
      queryClient.invalidateQueries({ queryKey: ['my-pto-requests'] });
      queryClient.invalidateQueries({ queryKey: ['my-pto-balance'] });
      resetForm();
      setModalOpen(false);
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to submit PTO request.',
        color: 'red',
      });
    },
  });

  function resetForm() {
    setLeaveType(null);
    setStartDate(null);
    setEndDate(null);
    setHoursPerDay(8);
    setReason('');
  }

  const isFormValid =
    leaveType !== null &&
    startDate !== null &&
    endDate !== null &&
    hoursPerDay !== '' &&
    Number(hoursPerDay) > 0 &&
    reason.trim().length > 0;

  const balances = balanceQuery.data?.balances ?? [];
  const requests = requestsQuery.data ?? [];

  // ---------- Render ----------

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>PTO Requests</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setModalOpen(true)}>
          Submit PTO Request
        </Button>
      </Group>

      {/* ===== PTO Balance Summary ===== */}
      <Paper p="md" shadow="sm" radius="md" withBorder>
        <Group gap="xs" mb="md">
          <IconBeach size={20} />
          <Text fw={600} size="lg">PTO Balances</Text>
        </Group>

        {balanceQuery.isLoading ? (
          <Center h={80}><Loader /></Center>
        ) : balanceQuery.isError ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            Failed to load PTO balances.
          </Alert>
        ) : !balances.length ? (
          <Text c="dimmed">No PTO balances found.</Text>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
            {balances.map((bal) => {
              const pct = bal.total > 0 ? (bal.used / bal.total) * 100 : 0;
              return (
                <Paper key={bal.leave_type} p="md" radius="md" withBorder>
                  <Text fw={600} size="sm" tt="capitalize" mb="xs">
                    {bal.leave_type}
                  </Text>
                  <Text size="xl" fw={700}>
                    {bal.available}
                    <Text span size="sm" c="dimmed" fw={400}>
                      {' '}/ {bal.total} hrs
                    </Text>
                  </Text>
                  <Progress
                    value={pct}
                    color={pct > 80 ? 'red' : pct > 50 ? 'yellow' : 'blue'}
                    size="sm"
                    mt="xs"
                  />
                  <Group gap="xs" mt="xs">
                    <Text size="xs" c="dimmed">Used: {bal.used}</Text>
                    {bal.pending > 0 && (
                      <Badge size="xs" color="yellow" variant="light">
                        {bal.pending} pending
                      </Badge>
                    )}
                  </Group>
                </Paper>
              );
            })}
          </SimpleGrid>
        )}
      </Paper>

      {/* ===== My Requests Table ===== */}
      <Paper p="md" shadow="sm" radius="md" withBorder>
        <Text fw={600} size="lg" mb="md">My Requests</Text>

        {requestsQuery.isLoading ? (
          <Center h={150}><Loader /></Center>
        ) : requestsQuery.isError ? (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            Failed to load PTO requests.
          </Alert>
        ) : !requests.length ? (
          <Text c="dimmed" ta="center" py="lg">
            No PTO requests found. Click "Submit PTO Request" to create one.
          </Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Leave Type</Table.Th>
                <Table.Th>Start Date</Table.Th>
                <Table.Th>End Date</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Hours/Day</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Submitted</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {requests.map((req) => (
                <Table.Tr key={req.id}>
                  <Table.Td>
                    <Text tt="capitalize">{req.leave_type}</Text>
                  </Table.Td>
                  <Table.Td>{dayjs(req.start_date).format('MM/DD/YYYY')}</Table.Td>
                  <Table.Td>{dayjs(req.end_date).format('MM/DD/YYYY')}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>{req.hours_per_day}</Table.Td>
                  <Table.Td>
                    <Badge color={statusColor(req.status)} variant="light" size="sm">
                      {req.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{dayjs(req.created_at).format('MM/DD/YYYY')}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      {/* ===== Submit PTO Modal ===== */}
      <Modal
        opened={modalOpen}
        onClose={() => { setModalOpen(false); resetForm(); }}
        title="Submit PTO Request"
        size="md"
      >
        <Stack gap="md">
          <Select
            label="Leave Type"
            placeholder="Select leave type"
            data={LEAVE_TYPE_OPTIONS}
            value={leaveType}
            onChange={setLeaveType}
            required
          />
          <DateInput
            label="Start Date"
            placeholder="Select start date"
            value={startDate}
            onChange={setStartDate}
            minDate={new Date()}
            required
          />
          <DateInput
            label="End Date"
            placeholder="Select end date"
            value={endDate}
            onChange={setEndDate}
            minDate={startDate ?? new Date()}
            required
          />
          <NumberInput
            label="Hours Per Day"
            value={hoursPerDay}
            onChange={(val) => setHoursPerDay(val as number | '')}
            min={0.5}
            max={24}
            step={0.5}
            decimalScale={1}
            required
          />
          <Textarea
            label="Reason"
            placeholder="Briefly describe why you need time off"
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            minRows={3}
            required
          />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={() => { setModalOpen(false); resetForm(); }}>
              Cancel
            </Button>
            <Button
              onClick={() => submitMutation.mutate()}
              loading={submitMutation.isPending}
              disabled={!isFormValid}
            >
              Submit Request
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
